import React, { useRef, useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiPause, FiHeadphones } = FiIcons;

// Featured English narrators — labelled by narration style, not raw filenames.
export const NARRATORS = [
  { id: 'sarah',   name: 'Sarah',   style: 'Warm & literary',      desc: 'Measured, intimate — literary fiction and memoir.', src: '/voice-samples/sarah.mp3' },
  { id: 'charlie', name: 'Charlie', style: 'Authoritative',        desc: 'Grounded and credible — business and non-fiction.', src: '/voice-samples/charlie.mp3' },
  { id: 'river',   name: 'River',   style: 'Calm & neutral',       desc: 'Even, unhurried — how-to and long-form.',           src: '/voice-samples/river.mp3' },
  { id: 'harry',   name: 'Harry',   style: 'Youthful & bright',    desc: 'Energetic, clear — YA and upbeat non-fiction.',     src: '/voice-samples/harry.mp3' },
  { id: 'laura',   name: 'Laura',   style: 'Gentle & expressive',  desc: 'Soft, characterful — children’s books and fiction.', src: '/voice-samples/laura.mp3' },
];

const VoiceSampler = ({ voices = NARRATORS, title = 'Hear the voices', subtitle = 'Real sample narration — press play on any voice.' }) => {
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const toggle = (v) => {
    const a = audioRef.current;
    if (!a) return;
    if (playing === v.id) {
      a.pause();
      setPlaying(null);
      return;
    }
    a.src = v.src;
    a.currentTime = 0;
    a.play().then(() => setPlaying(v.id)).catch(() => setPlaying(null));
  };

  return (
    <div className="bg-white rounded-2xl border border-parchment-200 shadow-sm p-6 sm:p-7">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0">
          <SafeIcon icon={FiHeadphones} className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
      </div>
      <p className="text-sm text-gray-500 mb-5 ml-[52px]">{subtitle}</p>

      <div className="space-y-3">
        {voices.map((v) => {
          const active = playing === v.id;
          return (
            <div
              key={v.id}
              className={`flex items-center gap-4 rounded-xl border p-3 sm:p-4 transition-all ${active ? 'border-primary-400 bg-primary-50/50 shadow-sm' : 'border-gray-200 bg-parchment-50'}`}
            >
              <button
                type="button"
                onClick={() => toggle(v)}
                aria-label={active ? `Pause ${v.name}` : `Play ${v.name} sample`}
                className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-white transition-transform hover:scale-105 ${active ? 'bg-secondary-500' : 'bg-gradient-to-r from-primary-500 to-secondary-500'}`}
              >
                <SafeIcon icon={active ? FiPause : FiPlay} className="w-5 h-5" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-gray-900 truncate">{v.name}</span>
                  <span className="text-[11px] font-bold uppercase tracking-wide text-primary-700 bg-primary-50 px-2 py-0.5 rounded-full whitespace-nowrap">{v.style}</span>
                </div>
                <p className="text-sm text-gray-600 truncate">{v.desc}</p>
              </div>
              {/* animated waveform when playing */}
              <div className="hidden sm:flex items-end gap-0.5 h-7 w-20 flex-shrink-0">
                {[...Array(14)].map((_, k) => (
                  <div
                    key={k}
                    className={`w-1 rounded-full bg-gradient-to-t from-primary-400 to-secondary-300 ${active ? 'animate-pulse' : ''}`}
                    style={{ height: active ? `${30 + ((k * 7) % 9) * 8}%` : '18%', animationDelay: `${k * 60}ms` }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <audio ref={audioRef} onEnded={() => setPlaying(null)} preload="none" />
    </div>
  );
};

export default VoiceSampler;
